import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, Button, Typography, TextField, Alert, FormControlLabel, Checkbox, Link,
  Stepper, Step, StepLabel
} from '@mui/material';
import GenericForm from './GenericForm';
import { businessFormFields as staticBusinessFields } from '../forms/businessFormFields';

const steps = ['Dati Azienda', 'Contatti e Sede', 'Competenze e Password'];

export const BusinessForm = ({ onRegisterSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({ acceptedTerms: false, password: '', categories: [] });
  const [error, setError] = useState('');
  const [businessFields, setBusinessFields] = useState(staticBusinessFields);

  const isPasswordValid = (value) => /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/.test(value);

  useEffect(() => {
    // Carica le categorie all'avvio del form
    const loadCategories = async () => {
      try {
        const res = await axios.get('/api/categories');
        const categories = res.data;
        // Aggiorna i field con le categorie caricate
        const updatedFields = staticBusinessFields.map(field => {
          if (field.name === 'categories') {
            return { ...field, options: categories };
          }
          return field;
        });
        setBusinessFields(updatedFields);
      } catch (err) {
        console.error('Errore caricamento categorie:', err);
      }
    };
    loadCategories();
  }, []);

  const handleNext = () => {
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validazione checkbox
    if (!formData.acceptedTerms) {
      setError('Devi accettare i Termini di Servizio e la Privacy Policy.');
      return;
    }
    
    if (!formData.password || !isPasswordValid(formData.password)) {
      setError('La password deve contenere almeno 8 caratteri, una lettera, un numero e un simbolo.');
      return;
    }
    
    try {
      // formData ora contiene sia i dati del GenericForm che la password
      await axios.post('/api/auth/signup/business', formData);
      onRegisterSuccess && onRegisterSuccess({ type: 'business' });
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('Si è verificato un errore imprevisto. Riprova.');
      }
    }
  };

  // Suddivisione dei campi per step
  const step1Fields = businessFields.filter(f => ['name', 'contactPerson', 'sector'].includes(f.name));
  const step2Fields = businessFields.filter(f => ['email', 'phone', 'address', 'city', 'province'].includes(f.name));
  const step3Order = ['categories', 'description'];
  const step3Fields = step3Order.map((name) => businessFields.find((f) => f.name === name)).filter(Boolean);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Registrazione Azienda Partner</Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 3 }} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {activeStep === 0 && (
        <Box>
          <GenericForm
            fields={step1Fields}
            formData={formData}
            setFormData={setFormData}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" onClick={handleNext}>Avanti</Button>
          </Box>
        </Box>
      )}

      {activeStep === 1 && (
        <Box>
          <GenericForm
            fields={step2Fields}
            formData={formData}
            setFormData={setFormData}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={handleBack}>Indietro</Button>
            <Button variant="contained" onClick={handleNext}>Avanti</Button>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box component="form" onSubmit={handleSubmit}>
          <GenericForm
            fields={step3Fields}
            formData={formData}
            setFormData={setFormData}
          />

          <TextField
            sx={{ mt: 2 }}
            name="password"
            label="Password"
            type="password"
            autoComplete="new-password"
            value={formData.password || ''} 
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            fullWidth
            required
          />
          
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={formData.acceptedTerms}
                onChange={(e) => setFormData(prev => ({ ...prev, acceptedTerms: e.target.checked }))}
              />
            }
            label={
              <Typography variant="body2">
                Accetto i{' '}
                <Link href="/privacy-terms" target="_blank" rel="noopener noreferrer" sx={{ color: '#417a2a' }}>
                  Termini di Servizio e Privacy Policy
                </Link>
              </Typography>
            }
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={handleBack}>Indietro</Button>
            <Button type="submit" variant="contained">
              Invia per Approvazione
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};